<template>
  <!-- A My BF Fulfilment card from 2026 on: the 5-month pledge (F1, F2), Sow (F2, F3)
       or a one-time pledge (F4), as the F frames draw it. -->
  <section
    class="w-full flex flex-col overflow-hidden rounded-[14px] border border-solid border-brown-75 bg-white"
    :data-test="`bf-fulfilment-card-${card.key}`"
  >
    <h2
      class="flex items-center gap-[10px] bg-brown-700 text-white font-bold px-[22px] md:px-[24px] lg:px-[28px] py-[14px] md:py-[16px] lg:py-[18px] text-[1.125rem] md:text-[1.25rem] lg:text-[1.375rem] leading-[1.5rem] md:leading-[1.625rem] lg:leading-[1.75rem]"
    >
      <span
        :class="[
          'size-[32px] shrink-0 rounded-full flex items-center justify-center text-[16px] leading-none',
          tone.circle,
        ]"
        aria-hidden="true"
        >{{ tone.emoji }}</span
      >
      {{ card.title }}
    </h2>

    <!-- A couple pledge: the card's first row names the partner, as the F1 couple
         frame draws it, with the frames' rule under it. -->
    <div
      v-if="couple"
      class="flex items-center px-[22px] md:px-[24px] lg:px-[28px] py-[16px] text-[17px] md:text-[1.125rem] lg:text-[1.25rem] leading-[24px] md:leading-[1.5rem] lg:leading-[1.625rem] text-grey-800 break-words"
      data-test="bf-fulfilment-couple"
    >
      <span>
        👫 Pledging as a couple<template v-if="couple.partnerName">
          with <span data-test="bf-fulfilment-partner-name">{{ couple.partnerName }}</span>
        </template>
      </span>
    </div>

    <!-- Sow: the member's own words. -->
    <div
      v-if="card.note"
      :class="[
        'flex flex-col gap-[4px] bg-white px-[22px] md:px-[24px] lg:px-[28px] pt-[16px]',
        card.sections.length ? 'pb-[6px]' : 'pb-[16px]',
      ]"
      data-test="bf-fulfilment-card-note"
    >
      <span class="text-[0.8125rem] leading-[1.125rem] text-prime-placeholder"
        >I pledged to sow</span
      >
      <span
        class="text-[1rem] leading-[1.375rem] font-semibold text-prime-ink whitespace-pre-line break-words"
        >{{ card.note }}</span
      >
    </div>

    <template v-for="(section, index) in card.sections" :key="section.title">
      <!-- The frames' inset rule between closed rows, and under the sow note. An open
           row's beige is its own edge, so there is none beside one (F1, F3). -->
      <div
        v-if="hasRuleBefore(index)"
        class="h-px mx-[22px] md:mx-[24px] lg:mx-[28px] bg-brown-75"
        data-test="bf-fulfilment-card-divider"
      />
      <FulfilmentAmountRow
        :class="{ 'mt-[10px]': index === 0 && card.note && isOpen(section) }"
        :title="section.title"
        :total="section.total"
        :rows="section.rows"
        :info="section.info"
        :open="isOpen(section)"
        @toggle="toggle(section)"
        @info="$emit('info', $event)"
      />
    </template>

    <div
      v-if="card.percentage != null"
      class="w-full flex flex-row justify-between border-t border-solid border-brown-75 text-[17px] md:text-[1.125rem] lg:text-[1.25rem] leading-[24px] md:leading-[1.5rem] lg:leading-[1.625rem] text-grey-800 py-[14px] md:py-[16px] lg:py-[18px] pl-[22px] md:pl-[24px] lg:pl-[28px] pr-[46px] md:pr-[52px] lg:pr-[60px]"
    >
      <span>Percentage fulfilled</span>
      <span data-test="bf-fulfilment-card-percentage">{{ card.percentage }}%</span>
    </div>
  </section>
</template>

<script>
import { BF_PLEDGE_CARD_TONES } from '@/constants/MyGiving/building-fund-pledge';
import FulfilmentAmountRow from './FulfilmentAmountRow.vue';

export default {
  name: 'FulfilmentCard',

  components: { FulfilmentAmountRow },

  props: {
    // One of fulfilmentCards()'s cards (src/utils/bf-fulfilment.js).
    card: {
      type: Object,
      required: true,
    },
    // pledgeSummary()'s `couple` ({ partnerName }) on a couple pledge's card, else null.
    couple: {
      type: Object,
      default: null,
    },
  },

  // A row's ⓘ was tapped, with which explainer it is for; the page shows it.
  emits: ['info'],

  data() {
    return {
      // The titles of the rows that are open. Every row starts closed, as F1b.
      openRows: [],
    };
  },

  computed: {
    tone() {
      return BF_PLEDGE_CARD_TONES[this.card.tone];
    },
  },

  methods: {
    isOpen(section) {
      return this.openRows.includes(section.title);
    },

    toggle(section) {
      this.openRows = this.isOpen(section)
        ? this.openRows.filter(title => title !== section.title)
        : [...this.openRows, section.title];
    },

    // A rule goes between two closed rows, and between the sow note or the couple
    // row and a closed row.
    hasRuleBefore(index) {
      if (this.isOpen(this.card.sections[index])) return false;
      if (index === 0) return Boolean(this.card.note || this.couple);
      return !this.isOpen(this.card.sections[index - 1]);
    },
  },
};
</script>
